import { apiRequest } from '../util/apiUtils';
import { withRetries } from '../util/retry';
import { normalizeOriginalForUpload } from './imageNormalizeService';

let stagingStatusCache = null;
let stagingStatusPromise = null;

export const isStagingObjectKey = (value) =>
  typeof value === 'string' && value.startsWith('staging/');

export async function fetchStagingUploadStatus(force = false) {
  if (!force && stagingStatusCache) {
    return stagingStatusCache;
  }

  if (!force && stagingStatusPromise) {
    return stagingStatusPromise;
  }

  stagingStatusPromise = apiRequest('get', '/media/staging-upload-status')
    .then((res) => {
      const payload = res?.data?.data || res?.data;
      stagingStatusCache = {
        enabled: !!payload?.enabled,
        configured: !!payload?.configured,
        // Undefined (older API without the field) => false => legacy multipart.
        directUpload: !!payload?.directUpload,
        // Kill switch for the auto crop; null when the API does not say.
        smartCrop: typeof payload?.smartCrop === 'boolean' ? payload.smartCrop : null,
      };
      return stagingStatusCache;
    })
    .catch(() => {
      stagingStatusCache = { enabled: false, configured: false, directUpload: false, smartCrop: null };
      return stagingStatusCache;
    })
    .finally(() => {
      stagingStatusPromise = null;
    });

  return stagingStatusPromise;
}

export async function isStagingUploadEnabled() {
  const status = await fetchStagingUploadStatus();
  return !!(status.enabled && status.configured);
}

/** True when the server has enabled presigned browser-direct S3 uploads. */
export async function isDirectStagingUploadEnabled() {
  const status = await fetchStagingUploadStatus();
  return !!(status.enabled && status.configured && status.directUpload);
}

/** Simple in-flight concurrency limiter (finalize calls hit the API's 2-vCPU box). */
function createConcurrencyLimiter(maxConcurrent) {
  let active = 0;
  const queue = [];
  const pump = () => {
    if (active >= maxConcurrent || queue.length === 0) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        pump();
      });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      pump();
    });
}

/**
 * Client-side finalize cap. S3 PUTs are cheap and run up to Uppy's limit, but
 * every finalize downloads + Sharp-normalizes on the API, so cap at 2 in flight.
 */
export const STAGING_FINALIZE_CONCURRENCY = 2;
const finalizeLimiter = createConcurrencyLimiter(STAGING_FINALIZE_CONCURRENCY);

function newClientUploadId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Step 1: ask the API for a presigned PUT URL + staging key.
 * `clientUploadId` defeats apiRequest's in-flight dedup so parallel original
 * uploads each get a distinct key.
 */
export async function requestStagingUploadInit({
  bookId,
  variant = 'original',
  isCover = false,
  mediaId = null,
  contentLength = null,
  filename = null,
}) {
  const body = {
    bookId: String(bookId),
    variant: variant === 'original' ? 'original' : 'cropped',
    clientUploadId: newClientUploadId(),
  };
  if (isCover) body.isCover = 'true';
  if (mediaId) body.mediaId = String(mediaId);
  if (contentLength != null) body.contentLength = contentLength;
  if (filename) body.originalFilename = filename;

  const res = await apiRequest('post', '/media/staging-upload-init', { body });
  const payload = res?.data;
  if (!payload?.success || !payload?.data?.uploadUrl || !payload?.data?.stagingKey) {
    throw new Error(stagingUploadErrorMessage(res));
  }
  return payload.data;
}

/** Attempts for the browser -> S3 PUT (matches the Uppy uploader). */
const PUT_ATTEMPTS = 3;

/** Step 2: PUT the raw file straight to S3. Bypasses apiRequest (no auth header). */
export async function putFileToPresignedUrl(uploadUrl, file, headers = {}) {
  let res;
  try {
    res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: headers || {},
    });
  } catch (err) {
    throw Object.assign(
      new Error('We lost the connection while saving this photo. Please check your internet and try again.'),
      { status: 0, cause: err },
    );
  }
  if (!res.ok) {
    if (res.status === 403) {
      throw Object.assign(new Error('The upload for this photo timed out. Please try again.'), {
        status: 403,
      });
    }
    throw Object.assign(
      new Error(`We couldn't save this photo. Please try again. (Error ${res.status})`),
      { status: res.status },
    );
  }
}

/**
 * PUT with the same retry policy as the Uppy uploader: network drops and 5xx
 * repeat on the same presigned URL, a 403 mints a fresh one, other 4xx fail.
 * Returns the (possibly refreshed) init so the caller finalizes the right key.
 */
async function putWithRetry({ init, requestInit, file, filename }) {
  let current = init;
  let attempts = 0;
  await withRetries(
    (attempt) => {
      attempts = attempt;
      return putFileToPresignedUrl(current.uploadUrl, file, current.headers);
    },
    {
      attempts: PUT_ATTEMPTS,
      shouldRetry: (err) => err?.status === 0 || err?.status === 403 || err?.status >= 500,
      onRetry: async (err, attempt) => {
        // eslint-disable-next-line no-console
        console.warn(
          `[STAGING-PUT] attempt ${attempt} failed for ${filename} (status ${err?.status}): ${err?.message}; retrying`,
        );
        if (err?.status === 403) current = await requestInit();
      },
    },
  );
  return { init: current, attempts };
}

/** Step 3: tell the API the object landed; it normalizes + writes the DB row. */
export async function finalizeStagingUpload({
  bookId,
  stagingKey,
  variant = 'original',
  isCover = false,
  mediaId = null,
  filename = null,
  clientTimings = null,
  clientNormalized = false,
  cropData = null,
  cropReview = null,
}) {
  const body = {
    bookId: String(bookId),
    stagingKey,
    variant: variant === 'original' ? 'original' : 'cropped',
  };
  if (isCover) body.isCover = 'true';
  if (mediaId) body.mediaId = String(mediaId);
  // The crop rectangle and review live on the media record so they survive
  // reloads and re-entry; the server never computes them.
  if (cropData) body.cropData = cropData;
  if (cropReview) body.cropReview = cropReview;
  if (filename) body.originalFilename = filename;
  if (clientTimings) body.clientTimings = clientTimings;
  if (clientNormalized) body.clientNormalized = 'true';

  const run = () => apiRequest('post', '/media/staging-upload-complete', { body });
  // One retry on transient 5xx / queue-full.
  let res = await finalizeLimiter(run);
  const status = res?.status;
  if (status === 503 || (status >= 500 && status < 600)) {
    await new Promise((r) => setTimeout(r, 600));
    res = await finalizeLimiter(run);
  }

  const payload = res?.data;
  if (!payload?.success || !payload?.data?.stagingKey) {
    throw new Error(stagingUploadErrorMessage(res));
  }

  const { stagingKey: key, previewUrl } = payload.data;
  return {
    url: previewUrl,
    previewUrl,
    publicId: key,
    stagingKey: key,
    mediaId: payload.data.mediaId || null,
    original_filename: payload.data.original_filename || filename,
  };
}

/**
 * Presigned direct-to-S3 upload: init -> PUT to S3 -> finalize.
 * Same return shape as the legacy multipart uploadToStaging.
 */
export async function uploadToStagingDirect(
  fileOrBlob,
  bookId,
  variant = 'original',
  filename = 'image.jpg',
  { isCover = false, mediaId = null, cropData = null, cropReview = null } = {},
) {
  if (!bookId) {
    throw new Error('bookId is required for staging uploads');
  }

  let file = await prepareFileForStagingUpload(fileOrBlob, filename);

  // Variant A: normalize originals client-side (upright JPEG, long-edge cap)
  // so the API can finalize metadata-only. Crops are already browser JPEGs.
  let clientNormalized = false;
  let normalizeTimings = {};
  if (variant === 'original') {
    const normalized = await normalizeOriginalForUpload(file);
    file = normalized.file;
    clientNormalized = normalized.clientNormalized;
    normalizeTimings = {
      normalize_start: normalized.normalize_start,
      normalize_end: normalized.normalize_end,
      normalized_bytes: normalized.normalized_bytes,
    };
  }

  // Per-file phase timings. put_start/put_end are wall-clock ms so the batch
  // PUT phase can be measured as overlap (earliest put_start → latest
  // put_end), not the sum of put_ms.
  const initStart = Date.now();
  const requestInit = () =>
    requestStagingUploadInit({
      bookId,
      variant,
      isCover,
      mediaId,
      contentLength: file.size,
      filename: file.name,
    });
  const initMs = Date.now() - initStart;

  const putStart = Date.now();
  const { init, attempts: putAttempts } = await putWithRetry({
    init: await requestInit(),
    requestInit,
    file,
    filename: file.name,
  });
  const putEnd = Date.now();

  const clientTimings = {
    init_ms: initMs,
    put_ms: putEnd - putStart,
    put_start: putStart,
    put_end: putEnd,
    put_attempts: putAttempts,
    bytes: fileOrBlob.size,
    ...normalizeTimings,
  };
  // eslint-disable-next-line no-console
  console.log(
    `[STAGING-TIMING] ${JSON.stringify({
      stagingKey: init.stagingKey,
      variant,
      isCover,
      filename: file.name,
      clientNormalized,
      ...clientTimings,
    })}`,
  );

  return finalizeStagingUpload({
    bookId,
    stagingKey: init.stagingKey,
    variant,
    isCover,
    mediaId,
    filename: file.name,
    clientTimings,
    clientNormalized,
    cropData,
    cropReview,
  });
}

function toFile(fileOrBlob, filename) {
  if (fileOrBlob instanceof File) {
    return fileOrBlob;
  }
  return new File([fileOrBlob], filename, {
    type: fileOrBlob.type || 'image/jpeg',
  });
}

/** Normalize blob/File for multipart upload — no re-encoding (same as Cloudinary path). */
export async function prepareFileForStagingUpload(
  fileOrBlob,
  filename = 'image.jpg',
) {
  return toFile(fileOrBlob, filename);
}

/** @deprecated Use prepareFileForStagingUpload */
export async function compressImageIfLarge(fileOrBlob, filename = 'image.jpg') {
  return prepareFileForStagingUpload(fileOrBlob, filename);
}

function stagingUploadErrorMessage(res) {
  const status = res?.status;
  const body = res?.data;

  if (status === 413) {
    return 'Photo is too large for the server upload limit. Please choose a smaller image.';
  }
  if (status === 401 || status === 403) {
    return body?.message || 'Upload was denied. Refresh the page and sign in again.';
  }

  return body?.message || body?.error || 'Staging upload failed';
}

/**
 * Upload a file to staging. Routes to presigned direct-to-S3 when the server
 * has it enabled, otherwise falls back to the legacy multipart-through-API path.
 * Both return the same result shape.
 */
export async function uploadToStaging(
  fileOrBlob,
  bookId,
  variant = 'original',
  filename = 'image.jpg',
  options = {},
) {
  if (await isDirectStagingUploadEnabled()) {
    return uploadToStagingDirect(fileOrBlob, bookId, variant, filename, options);
  }
  return uploadToStagingLegacy(fileOrBlob, bookId, variant, filename, options);
}

async function uploadToStagingLegacy(
  fileOrBlob,
  bookId,
  variant = 'original',
  filename = 'image.jpg',
  { isCover = false, mediaId = null, cropData = null, cropReview = null } = {},
) {
  if (!bookId) {
    throw new Error('bookId is required for staging uploads');
  }

  const file = await prepareFileForStagingUpload(fileOrBlob, filename);
  const formData = new FormData();
  formData.append('file', file);
  formData.append('bookId', String(bookId));
  formData.append('variant', variant === 'original' ? 'original' : 'cropped');
  if (isCover) {
    formData.append('isCover', 'true');
  }
  if (mediaId) {
    formData.append('mediaId', String(mediaId));
  }
  if (cropData) formData.append('cropData', JSON.stringify(cropData));
  if (cropReview) formData.append('cropReview', JSON.stringify(cropReview));

  const res = await apiRequest(
    'post',
    '/media/upload-staging',
    { body: formData },
    'multipart/form-data',
  );

  const body = res?.data;
  if (!body?.success || !body?.data?.stagingKey) {
    throw new Error(stagingUploadErrorMessage(res));
  }

  const { stagingKey, previewUrl } = body.data;

  return {
    url: previewUrl,
    previewUrl,
    publicId: stagingKey,
    stagingKey,
    mediaId: body.data.mediaId || null,
    original_filename: body.data.original_filename || file.name,
  };
}

export function buildCropUploadRef(uploadResult) {
  if (!uploadResult) return null;

  const stagingKey = uploadResult.stagingKey || null;
  const url = uploadResult.url || uploadResult.previewUrl || null;
  const publicId = uploadResult.publicId || stagingKey || null;

  if (!url || !publicId) return null;

  return {
    url,
    publicId,
    ...(stagingKey ? { stagingKey } : {}),
  };
}
